/**
 * Best-effort, web-only "grab the agent's attention" helper used by the
 * activity watchdog. In Electron these calls degrade gracefully; a richer
 * native foreground (show/focus) is a separate task in the i2x-coach-desktop
 * repo, where that capability already exists.
 *
 * It does two things, each guarded so it is a no-op when unavailable:
 *  - window.focus()  — best effort, often blocked by the browser
 *  - Notification    — only if permission was already granted (never prompts)
 */

let activeNotification: Notification | null = null;

const hasWindow = (): boolean => typeof window !== 'undefined';

const showNotification = (title: string, body: string) => {
    if (!hasWindow() || typeof Notification === 'undefined') {
        return;
    }
    if (Notification.permission !== 'granted') {
        return;
    }
    try {
        activeNotification = new Notification(title, { body, requireInteraction: true });
        activeNotification.onclick = () => {
            if (hasWindow()) {
                window.focus();
            }
        };
    } catch {
        activeNotification = null;
    }
};

export interface AttentionRequest {
    title: string;
    body: string;
}

export const requestUserAttention = ({ title, body }: AttentionRequest): void => {
    if (hasWindow()) {
        // In the desktop app this brings the window to the foreground; in the
        // browser it's a best-effort focus (often blocked).
        try {
            window.electronAPI?.focusWindow?.();
        } catch {
            // ignore
        }
        try {
            window.focus();
        } catch {
            // ignore — browsers frequently block programmatic focus
        }
    }
    showNotification(title, body);
};

export const clearUserAttention = (): void => {
    if (activeNotification) {
        try {
            activeNotification.close();
        } catch {
            // ignore
        }
        activeNotification = null;
    }
};
